<template>
  <div class="content">
    <TextBody :text="title" tag="span" :body-type="4" weight="medium" />
    <BbInfiniteScroll :handler="loadAssets" :distance="240" :disabled="disableInfiniteScroll" class="images-container">
      <ImagesCard
        v-for="(image, index) in userImages"
        :key="image.id"
        :image="image"
        :index="index + 1"
        @delete-image="onDeleteImage"
      />
      <template v-if="isLoading && hasMoreImages">
        <BbSkeletonLoader type="card" class="image-skeleton" />
        <BbSkeletonLoader type="card" class="image-skeleton" />
        <BbSkeletonLoader type="card" class="image-skeleton" />
      </template>
    </BbInfiniteScroll>
    <div v-if="isLoading && userImages.length === 0" class="images-container">
      <BbSkeletonLoader type="card" class="image-skeleton" />
      <BbSkeletonLoader type="card" class="image-skeleton" />
      <BbSkeletonLoader type="card" class="image-skeleton" />
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref } from 'vue';

import useImageGeneration from '~/composables/useImageGeneration';
import TextBody from '~/components/common/TextBody.vue';
import ImagesCard from '~/components/userImages/ImagesCard.vue';

export default {
  components: { TextBody, ImagesCard },

  setup() {
    const { t } = useI18n();
    const { retrieveImages, deleteUserImageById } = useImageGeneration();

    const isLoading = ref(false);
    const hasMoreImages = ref(true);
    const offset = ref(0);
    const limit = 20;
    const userImages = ref([]);

    const title = computed(() => `${t('account.userImages')} (${userImages.value.length})`);
    const disableInfiniteScroll = computed(() => isLoading.value || !hasMoreImages.value);

    const loadAssets = async () => {
      isLoading.value = true;

      const { images: newImages, total } = await retrieveImages({
        from: offset.value,
        size: limit,
      });

      userImages.value = [...userImages.value, ...newImages];
      hasMoreImages.value = offset.value + limit < total || offset.value === 0;
      offset.value = offset.value + limit;
      isLoading.value = false;
    };

    const onDeleteImage = async (imageId) => {
      await deleteUserImageById(imageId);
      userImages.value = userImages.value.filter((image) => image.id !== imageId);
    };

    onMounted(() => {
      loadAssets();
    });

    return { title, userImages, isLoading, hasMoreImages, disableInfiniteScroll, loadAssets, onDeleteImage };
  },
};
</script>

<style scoped lang="scss">
.content {
  background: $bb-neutral-0;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: $bb-spacing-5 $bb-spacing-6;

  :deep(.body-4) {
    color: $bb-neutral-1 !important;
    font-size: 18px !important;
    line-height: 22px;
    letter-spacing: -0.3px;
  }

  .images-container {
    gap: $bb-spacing-8;
    margin-top: $bb-spacing-10;
    display: flex;
    flex-wrap: wrap;

    .image-skeleton {
      width: 291px;
      height: 288px;
    }

    @media (max-width: 992px) {
      grid-template-columns: auto;
    }
  }
}
</style>
